import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'

interface FieldProps {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  optional?: boolean
  required?: boolean
  children: ReactNode
  className?: string
}

export function Field({
  label,
  hint,
  error,
  optional,
  required,
  children,
  className = '',
}: FieldProps) {
  return (
    <label className={`block space-y-1.5 ${className}`}>
      {label ? (
        <span className="flex items-baseline justify-between text-sm font-medium text-fg">
          <span>
            {label}
            {required && <span className="ml-0.5 text-danger">*</span>}
          </span>
          {optional && <span className="text-xs font-normal text-fg-subtle">Optional</span>}
        </span>
      ) : null}
      {children}
      {error ? <span className="block text-xs text-danger">{error}</span> : null}
      {hint && !error ? <span className="block text-xs text-fg-subtle">{hint}</span> : null}
    </label>
  )
}

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  numeric?: boolean
  prefix?: ReactNode
  suffix?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className = '', numeric, prefix, suffix, ...rest },
  ref,
) {
  const cls = numeric ? 'field-input-numeric' : 'field-input'
  if (!prefix && !suffix) {
    return <input ref={ref} className={`${cls} ${className}`} {...rest} />
  }
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-surface shadow-sm focus-within:border-[var(--color-primary)] focus-within:ring-1 focus-within:ring-[var(--color-primary)]">
      {prefix ? (
        <span className="pl-3 text-sm text-fg-subtle">{prefix}</span>
      ) : null}
      <input
        ref={ref}
        className={`flex-1 bg-transparent px-3 py-2 text-sm text-fg placeholder-fg-faint focus:outline-none ${
          numeric ? 'font-mono' : ''
        } ${prefix ? 'pl-1' : ''} ${suffix ? 'pr-1' : ''} ${className}`}
        {...rest}
      />
      {suffix ? <span className="pr-3 text-xs text-fg-subtle">{suffix}</span> : null}
    </div>
  )
})

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(function Select({ className = '', children, ...rest }, ref) {
  return (
    <select ref={ref} className={`field-input pr-8 cursor-pointer ${className}`} {...rest}>
      {children}
    </select>
  )
})

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className = '', ...rest }, ref) {
  return <textarea ref={ref} className={`field-input resize-y ${className}`} {...rest} />
})

export function Checkbox({
  label,
  checked,
  onChange,
  disabled,
  className = '',
}: {
  label: ReactNode
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  className?: string
}) {
  return (
    <label
      className={`group inline-flex cursor-pointer items-center gap-2 text-sm text-fg ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      } ${className}`}
    >
      <span
        className={`relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
          checked
            ? 'border-[var(--color-primary)] bg-[var(--color-primary)]'
            : 'border-border-strong bg-surface'
        }`}
      >
        {checked && (
          <svg
            className="h-3 w-3 text-white"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="M2.5 6.5l2.5 2.5 4.5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span>{label}</span>
    </label>
  )
}
